import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, mergeMap, of, switchMap } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ModalWindowsService } from '../../../modals/services/modal-windows.service';
import { SnackBarType } from '../../../shared/components/snack-bar/snack-bar.component';
import { NOTIFICATIONS } from '../../../shared/constants';
import { BadResponseWithError } from '../../../shared/models/bad-response-with-error.interface';
import { NotificationsService } from '../../../shared/services/notifications.service';
import { WithdrawalRequestStatusType, TransferType } from '../../models/enums/wallet.enum';
import {
    WalletPaymentAccount,
    WalletPaymentTransaction,
    WithdrawalRequest,
    WithdrawalRequestsWithPagination,
} from '../../models/wallet.interface';
import { WalletService } from '../../services/wallet.service';
import { WalletActions } from '../actions/actions-type.action';
import { ConsoleLoggerService } from '../../../shared/services/console-logger.service';

@Injectable()
export class WalletEffect {
    private actions$ = inject(Actions);
    private modalService = inject(ModalWindowsService);
    private notificationsService = inject(NotificationsService);
    private walletService = inject(WalletService);
    // private logger = inject(ConsoleLoggerService);

    fetchProfilePaymentAccounts = createEffect(() =>
        this.actions$.pipe(
            ofType(WalletActions.fetchProfilePaymentAccounts),
            switchMap(({ payload }) =>
                this.walletService.fetchPaymentAccounts(payload.playerId).pipe(
                    map((response: WalletPaymentAccount[]) =>
                        {
                            return WalletActions.fetchProfilePaymentAccountsSuccess({
                                payload: response,
                            })
                        }
                    ),
                    catchError((response: BadResponseWithError) => {
                        this.notificationsService.openSnackBar({
                            title: response.error?.message,
                            type: SnackBarType.Error,
                            error: response.error,
                        });

                        return of(
                            WalletActions.fetchProfilePaymentAccountsFailure({
                                payload: response,
                            })
                        );
                    })
                )
            )
        )
    );

    fetchWalletTransactions = createEffect(() =>
        this.actions$.pipe(
            ofType(WalletActions.fetchWalletTransactions),
            switchMap(({ payload }) =>
                this.walletService
                    .fetchWalletTransactions(
                        payload.accountId,
                        payload.transferType
                    )
                    .pipe(
                        map((response: WalletPaymentTransaction[]) =>
                            WalletActions.fetchWalletTransactionsSuccess({
                                payload: response,
                            })
                        ),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: response.error?.message,
                                type: SnackBarType.Error,
                                error: response.error,
                            });

                            return of(
                                WalletActions.fetchWalletTransactionsFailure({
                                    payload: response,
                                })
                            );
                        })
                    )
            )
        )
    );

    fetchLPWalletTransactions = createEffect(() =>
        this.actions$.pipe(
            ofType(WalletActions.fetchLPWalletTransactions),
            switchMap(({ payload }) =>
                this.walletService
                    .fetchLPWalletTransactions(
                        payload.accountId,
                        payload.transferType
                    )
                    .pipe(
                        map((response: WalletPaymentTransaction[]) =>
                            WalletActions.fetchLPWalletTransactionsSuccess({
                                payload: response,
                            })
                        ),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: response.error?.message,
                                type: SnackBarType.Error,
                                error: response.error,
                            });

                            return of(
                                WalletActions.fetchLPWalletTransactionsFailure({
                                    payload: response,
                                })
                            );
                        })
                    )
            )
        )
    );


    sendWithdrawalRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WalletActions.sendWithdrawalRequest),
            exhaustMap((action) => {
                const request = (() => { const { type, ...request } = action as any; return request; })();
                const requestId = (request as any)?.requestId || null;
                const start = Date.now();
                // this.logger.info('WithdrawStore', 'sendWithdrawalRequest - action received', { requestId, bodyMasked: request });
                // this.logger.info('WithdrawStore', 'sendWithdrawalRequest - HTTP call begin', { requestId });
                return this.walletService
                    .sendWithdrawalRequest(request)
                    .pipe(
                        map(() => {
                            const elapsedMs = Date.now() - start;
                            // this.logger.success('Wallet', 'sendWithdrawalRequest - success', { requestId, status: 'ok', success: true, elapsedMs });
                            this.modalService.closeLoadingDialog();
                            this.notificationsService.openSnackBar({
                                title: 'Success',
                                message: 'Your request successfully sent',
                                type: SnackBarType.Success,
                            });

                            return WalletActions.sendWithdrawalRequestSuccess();
                        }),
                        catchError((response: BadResponseWithError) => {
                            const elapsedMs = Date.now() - start;
                            // this.logger.error('Wallet', 'sendWithdrawalRequest - error', {
                            //     requestId,
                            //     status: (response as any)?.status,
                            //     success: false,
                            //     error: response?.error,
                            //     elapsedMs,
                            // });
                            this.modalService.closeLoadingDialog();
                            this.notificationsService.openSnackBar({
                                title: response.error?.message,
                                type: SnackBarType.Error,
                                error: response.error,
                            });

                            return of(
                                WalletActions.sendWithdrawalRequestFailure({
                                    payload: response,
                                })
                            );
                        })
                    );
            })
        )
    );

    fetchWithdrawalRequests$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WalletActions.fetchWithdrawalRequests),
            switchMap(({ payload }) =>
                this.walletService
                    .fetchWithdrawalRequests(payload?.status)
                    .pipe(
                        map((items: WithdrawalRequestsWithPagination) =>
                            WalletActions.fetchWithdrawalRequestsSuccess({
                                payload: {
                                    items,
                                },
                            })
                        ),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: response.error?.message,
                                type: SnackBarType.Error,
                                error: response.error,
                            });

                            return of(
                                WalletActions.fetchWithdrawalRequestsFailure({
                                    payload: response,
                                })
                            );
                        })
                    )
            )
        )
    );

    // Store-flow logging (no dispatch) for withdrawal lifecycle
    logSendWithdrawalRequestSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WalletActions.sendWithdrawalRequestSuccess),
            tap(() => {
                // this.logger.success('WithdrawStore', 'sendWithdrawalRequestSuccess action observed');
            })
        ),
        { dispatch: false }
    );

    logSendWithdrawalRequestFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WalletActions.sendWithdrawalRequestFailure),
            tap(({ payload }) => {
                // this.logger.error('WithdrawStore', 'sendWithdrawalRequestFailure action observed', {
                //     error: payload?.error || payload
                // });
            })
        ),
        { dispatch: false }
    );

    logFetchWithdrawalRequests$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WalletActions.fetchWithdrawalRequests),
            tap(({ payload }) => {
                // this.logger.info('WithdrawStore', 'fetchWithdrawalRequests action observed', {
                //     status: payload?.status
                // });
            })
        ),
        { dispatch: false }
    );

    logFetchWithdrawalRequestsSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WalletActions.fetchWithdrawalRequestsSuccess),
            tap(({ payload }) => {
                const total = payload?.items?.total;
                const count = payload?.items?.items?.length;
                // this.logger.success('WithdrawStore', 'fetchWithdrawalRequestsSuccess action observed', { total, count });
            })
        ),
        { dispatch: false }
    );

    logFetchWithdrawalRequestsFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WalletActions.fetchWithdrawalRequestsFailure),
            tap(({ payload }) => {
                // this.logger.error('WithdrawStore', 'fetchWithdrawalRequestsFailure action observed', { error: payload?.error || payload });
            })
        ),
        { dispatch: false }
    );

    changeWithdrawalRequest = createEffect(() =>
        this.actions$.pipe(
            ofType(WalletActions.changeWithdrawalRequestStatus),
            switchMap(({ payload }) =>
                this.walletService
                    .changeWithdrawalRequestStatus(
                        payload.profileId,
                        payload.requestId,
                        payload.status
                    )
                    .pipe(
                        mergeMap((request: WithdrawalRequest) => [
                            WalletActions.changeWithdrawalRequestStatusSuccess({
                                payload: {
                                    request,
                                },
                            }),
                            WalletActions.fetchWithdrawalRequests({
                                payload: {
                                    status: WithdrawalRequestStatusType.Pending,
                                },
                            }),
                        ]),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: response.error?.message,
                                type: SnackBarType.Error,
                                error: response.error,
                            });

                            return of(
                                WalletActions.changeWithdrawalRequestStatusFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    )
            )
        )
    );

    depositAccount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WalletActions.depositProfileWallet),
            switchMap(({ payload }) =>
                this.walletService
                    .depositAccount({
                        profileId: payload.playerId,
                        amount: payload.amount,
                        currency: payload.amountCurrency,
                        description: payload.paymentDescription,
                    })
                    .pipe(
                        map((response: WalletPaymentTransaction) => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.ACCOUNT_WAS_SUCCESFULY_DEPOSITED,
                                type: SnackBarType.Success,
                            });
                            return WalletActions.depositProfileWalletSuccess();
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: response.error.message,
                                message:
                                    response.error.failures?.[0]?.errorMessage,
                                type: SnackBarType.Error,
                                error: response.error,
                            });

                            return of(
                                WalletActions.depositProfileWalletFailure({
                                    payload: response,
                                })
                            );
                        }),
                        tap(() => {
                            this.modalService.closeProfileWalletModal();
                        })
                    )
            )
        )
    );

    connectCryptoWallet = createEffect(() =>
        this.actions$.pipe(
            ofType(WalletActions.connectCryptoWallet),
            switchMap(({ payload }) =>
                this.walletService
                    .connectCryptoWallet(
                        payload.cryptoWalletNetwork,
                        payload.cryptoWalletAddress,
                        payload.cryptoWalletChainId,
                        payload.cryptoWalletName,
                        payload.profileId,
                    )
                    .pipe(
                        map((response: any) => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.CRYPTO_WAS_SUCCESSFULLY_CONNECTED,
                                type: SnackBarType.Success,
                            });
                            return WalletActions.connectCryptoWalletSuccess({
                                payload:{
                                    profileId: response.profileId,
                                    cryptoWalletId: response.id,
                                    cryptoWalletNetwork: response.network,
                                    cryptoWalletChainId: response.chainId,
                                    cryptoWalletAddress: response.walletAddress,
                                    cryptoWalletName: response.walletName,
                                }});
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: response.error.message,
                                message:
                                response.error.failures?.[0]?.errorMessage,
                                type: SnackBarType.Error,
                                error: response.error,
                            });

                            return of(
                                WalletActions.connectCryptoWalletFailure({
                                    payload: response,
                                })
                            );
                        }),
                        tap(() => {
                            this.modalService.closeConnectCryptoWalletModal();
                        })
                    )
            )
        )
    );
    disconnectCryptoWallet = createEffect(() =>
        this.actions$.pipe(
            ofType(WalletActions.disconnectCryptoWallet),
            switchMap(({ payload }) =>
                this.walletService
                    .disconnectCryptoWallet(
                        payload.profileId,
                        payload.walletId
                    )
                    .pipe(
                        map((response: any) => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.CRYPTO_WAS_SUCCESSFULLY_DISCONNECTED,
                                type: SnackBarType.Success,
                            });
                            return WalletActions.disconnectCryptoWalletSuccess({
                                payload:{
                                    profileId: payload.profileId,
                                }});
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: response.error.message,
                                message:
                                response.error.failures?.[0]?.errorMessage,
                                type: SnackBarType.Error,
                                error: response.error,
                            });

                            return of(
                                WalletActions.disconnectCryptoWalletFailure({
                                    payload: response,
                                })
                            );
                        })
                    )
            )
        )
    );
    fetchCryptoWallet = createEffect(() =>
        this.actions$.pipe(
            ofType(WalletActions.fetchCryptoWallet),
            switchMap(({ payload }) =>
                this.walletService
                    .fetchCryptoWallet(
                        payload.profileId,
                    )
                    .pipe(
                        map((response: any) => {
                            return WalletActions.fetchCryptoWalletSuccess({
                                payload:{
                                    profileId: response[0]?.profileId || null,
                                    cryptoWalletId: response[0]?.id || null,
                                    cryptoWalletNetwork: response[0]?.network || null,
                                    cryptoWalletChainId: response[0]?.walletChainId || null,
                                    cryptoWalletAddress: response[0]?.walletAddress || null,
                                    cryptoWalletName: response[0]?.walletName || null,
                                }});
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: response?.error?.message || NOTIFICATIONS.CRYPTO_WALLET_FETCH_ERROR,
                                type: SnackBarType.Success,
                            });
                            return of(
                                WalletActions.fetchCryptoWalletFailure({
                                    payload: response,
                                })
                            );
                        })
                    )
            )
        )
    );


    fetchTeamWallet = createEffect(() =>
        this.actions$.pipe(
            ofType(WalletActions.fetchTeamLPWallet),
            switchMap(({ payload }) =>
                this.walletService.fetchTeamWallet(payload.teamId).pipe(
                    map((response: WalletPaymentAccount) =>
                        WalletActions.fetchTeamLPWalletSuccess({
                            payload: response,
                        })
                    ),
                    catchError((response: BadResponseWithError) => {
                        this.notificationsService.openSnackBar({
                            title: response.error?.message,
                            type: SnackBarType.Error,
                            error: response.error,
                        });

                        return of(
                            WalletActions.fetchTeamLPWalletFailure({
                                payload: response,
                            })
                        );
                    })
                )
            )
        )
);

    depositLP = createEffect(() =>
        this.actions$.pipe(
            ofType(WalletActions.depositLPRequest),
            switchMap(({ payload }) =>
                this.walletService.depositLP(payload).pipe(
                    map(() =>{
                        this.notificationsService.openSnackBar({
                            title: NOTIFICATIONS.TOPUP_SUCCESS,
                            type: SnackBarType.Success,
                        });
                        return WalletActions.depositLPSuccess({
                            payload,
                        })

                    }

                    ),
                    catchError((response: BadResponseWithError) => {
                        this.notificationsService.openSnackBar({
                            title: response.error?.message,
                            type: SnackBarType.Error,
                            error: response.error,
                        });

                        return of(
                            WalletActions.depositLPFailure({
                                payload: response,
                            })
                        );
                    })
                )
            )
        )

    );



    fetchTeamWalletTransactions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WalletActions.fetchTeamLPWalletTransactions),
            switchMap(({ payload }) =>
                this.walletService
                    .fetchLPWalletTransactions(
                        payload.accountId,
                        payload.transferType
                    )
                    .pipe(
                        map((response: WalletPaymentTransaction[]) =>
                            WalletActions.fetchTeamLPWalletTransactionsSuccess({
                                payload: response,
                            })
                        ),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: response.error?.message,
                                type: SnackBarType.Error,
                                error: response.error,
                            });

                            return of(
                                WalletActions.fetchTeamLPWalletTransactionsFailure({
                                    payload: response,
                                })
                            );
                        })
                    )
            )
        )
    );









    refreshTeamAfterDeposit = createEffect(() =>
        this.actions$.pipe(
            ofType(WalletActions.depositLPSuccess),
            map(({ payload }) => {
                if (payload.receiverOwnerType === 'Team' && payload.receiverOwnerId) {
                    return WalletActions.fetchTeamLPWallet({
                        payload: { teamId: payload.receiverOwnerId }
                    });
                }
                return { type: '[Wallet] Noop after deposit' } as any;
            })
        )
    );



    fetchTeamTransactionsAfterWallet = createEffect(() =>
        this.actions$.pipe(
            ofType(WalletActions.fetchTeamLPWalletSuccess),
            map(({ payload }) =>
                WalletActions.fetchTeamLPWalletTransactions({
                    payload: { accountId: payload.accountId, transferType: TransferType.All }
                })
            )
        )
    );

    // After successful deposit, refresh profile payment accounts (player's balances)
    fetchProfileAccountsAfterDeposit = createEffect(() =>
        this.actions$.pipe(
            ofType(WalletActions.depositLPSuccess),
            map(({ payload }) => {
                const playerId = payload?.id as string | undefined;
                if (playerId) {
                    return WalletActions.fetchProfilePaymentAccounts({
                        payload: { playerId }
                    });
                }
                return { type: '[Wallet] Noop fetchProfile after deposit' } as any;
            })
        )
    );
}
